import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  BriefCampaignInfs,
  IBriefCampaignInfluencerSearchRequestV2,
} from "@shared/lib/types/briefCampains";
import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ExecutionStatusFilter,
  ExecutionStatusFilterEnum,
} from "@widgets/CampaignExecution/constants";
import { useEffect } from "react";
import { BRIEF_CAMPAIGNS_LIST_QUERY_KEY } from "../contsants";
import { CampaignService } from "../services/Campaign.service";

export type BriefCampaignsInfiniteParams = Omit<
  IBriefCampaignInfluencerSearchRequestV2,
  "pageNo"
> & {
  status?: ExecutionStatusFilter;
};

export function useGetBriefCampaigns(
  params: BriefCampaignsInfiniteParams,
  enabled = true
) {
  const { briefId, status, ...rest } = params;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery<
    BackednResponse<BriefCampaignInfs>,
    Error,
    InfiniteData<BackednResponse<BriefCampaignInfs>>,
    readonly unknown[],
    number
  >({
    queryKey: [
      ...BRIEF_CAMPAIGNS_LIST_QUERY_KEY(params.briefId),
      rest,
      status ?? ExecutionStatusFilterEnum.All,
    ],
    queryFn: ({ pageParam }) =>
      CampaignService.getBriefCampaigns(
        { ...rest, briefId, pageNo: pageParam },
        status || ExecutionStatusFilterEnum.All
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (!pagination?.hasNextPage) return undefined;
      return pagination.currentPage + 1;
    },
    enabled: enabled && !!briefId,
  });

  const errorMessage = isError && error?.message;

  useEffect(() => {
    if (errorMessage) {
      ToastService.error(errorMessage);
    }
  }, [errorMessage]);

  const allPages = data?.pages ?? [];
  const flatData = allPages.flatMap((p) => p?.data?.data ?? []);
  const lastPage = allPages[allPages.length - 1];
  const pagination = lastPage?.data?.pagination;

  return {
    flatData,
    pagination,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    queryKey: [
      ...BRIEF_CAMPAIGNS_LIST_QUERY_KEY(params.briefId),
      rest,
      status ?? ExecutionStatusFilterEnum.All,
    ],
  };
}
